import { useEffect, useRef, useState } from "react";
import api, { API_BASE, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { DownloadSimple, UploadSimple, FileCsv, CheckCircle, XCircle } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function ImportExportPage() {
  const [regions, setRegions] = useState([]);
  const [engineers, setEngineers] = useState([]);
  const [region, setRegion] = useState("all");
  const [status, setStatus] = useState("all");
  const [engineerId, setEngineerId] = useState("all");
  const [walkin, setWalkin] = useState("all"); // all | exclude | only — keep month-end reports clean
  const [pl, setPl] = useState("exclude");     // exclude | include | only
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [busy, setBusy] = useState(false);
  const [file, setFile] = useState(null);
  const [result, setResult] = useState(null);
  const [presets, setPresets] = useState([]);
  const fileRef = useRef(null);

  useEffect(() => {
    api.get("/regions").then((r) => setRegions(r.data)).catch(() => {});
    api.get("/engineers").then((r) => setEngineers(r.data)).catch(() => setEngineers([]));
    loadPresets();
  }, []);

  const loadPresets = async () => {
    try {
      const { data } = await api.get("/records/export-presets");
      setPresets(data || []);
    } catch { setPresets([]); }
  };

  const savePreset = async () => {
    const label = window.prompt("Preset name (e.g. 'Ali · Central · Last month')");
    if (!label || !label.trim()) return;
    try {
      const { data } = await api.post("/records/export-presets", {
        label: label.trim(),
        region, status, engineerId, dateFrom, dateTo,
      });
      setPresets([data, ...presets]);
      toast.success(`Saved preset "${data.label}"`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const applyPreset = (p) => {
    setRegion(p.region || "all");
    setStatus(p.status || "all");
    setEngineerId(p.engineerId || "all");
    setDateFrom(p.dateFrom || "");
    setDateTo(p.dateTo || "");
    toast.success(`Loaded "${p.label}"`);
  };

  const deletePreset = async (id) => {
    try {
      await api.delete(`/records/export-presets/${id}`);
      setPresets(presets.filter((p) => p.id !== id));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const buildParams = () => {
    const params = {};
    if (region !== "all") params.region = region;
    if (status !== "all") params.status = status;
    if (engineerId !== "all") params.engineer_id = engineerId;
    if (walkin !== "all") params.walkin = walkin;
    if (pl !== "exclude") params.pl = pl;
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    return params;
  };

  const resetFilters = () => {
    setRegion("all"); setStatus("all"); setEngineerId("all"); setWalkin("all"); setPl("exclude");
    setDateFrom(""); setDateTo("");
  };

  const setThisMonth = () => {
    const d = new Date();
    const first = new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
    const last  = new Date(d.getFullYear(), d.getMonth() + 1, 0).toISOString().slice(0, 10);
    setDateFrom(first); setDateTo(last);
  };

  const setLastMonth = () => {
    const d = new Date();
    const first = new Date(d.getFullYear(), d.getMonth() - 1, 1).toISOString().slice(0, 10);
    const last  = new Date(d.getFullYear(), d.getMonth(), 0).toISOString().slice(0, 10);
    setDateFrom(first); setDateTo(last);
  };

  const downloadCsv = async () => {
    setBusy(true);
    try {
      const res = await api.get("/records/export.csv", { params: buildParams(), responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      const disp = res.headers["content-disposition"] || "";
      const match = disp.match(/filename="?([^"]+)"?/i);
      a.download = match ? match[1] : `parts-records-${Date.now()}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Export downloaded");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const downloadAdjustments = async (fmt) => {
    setBusy(true);
    try {
      const res = await api.get(`/inventory/adjustments/export.${fmt}`, { responseType: "blob" });
      const mime = fmt === "xlsx"
        ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        : "text/csv";
      const url = URL.createObjectURL(new Blob([res.data], { type: mime }));
      const a = document.createElement("a");
      a.href = url;
      const disp = res.headers["content-disposition"] || "";
      const match = disp.match(/filename="?([^"]+)"?/i);
      a.download = match ? match[1] : `inventory-adjustments-${Date.now()}.${fmt}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Adjustments export downloaded");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const downloadXlsx = async () => {
    setBusy(true);
    try {
      const res = await api.get("/records/export.xlsx", { params: buildParams(), responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }));
      const a = document.createElement("a");
      a.href = url;
      const disp = res.headers["content-disposition"] || "";
      const match = disp.match(/filename="?([^"]+)"?/i);
      a.download = match ? match[1] : `parts-records-${Date.now()}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Excel export downloaded");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const doImport = async () => {
    if (!file) return toast.error("Choose a CSV file first");
    setBusy(true);
    setResult(null);
    try {
      const form = new FormData();
      form.append("file", file);
      const res = await api.post("/records/import", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(res.data);
      toast.success(`${res.data.created} record(s) imported`);
      setFile(null);
      if (fileRef.current) fileRef.current.value = "";
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const sample = `engineer_email,engineer_name,region,parts_taken,taken_at,parts_returned,returned_at,status,notes
engineer@example.com,,North,"Cable:2; Sensor:1",2026-02-01T09:30:00Z,"Cable:2[unused]; Sensor:1[used]",2026-02-01T18:00:00Z,returned,Job #4412`;

  return (
    <div className="min-h-screen">
      <div className="border-b border-neutral-200 bg-white/80 backdrop-blur-xl sticky top-0 z-10">
        <div className="px-10 py-6">
          <div className="label-eyebrow">Data</div>
          <h1 className="font-display text-4xl tracking-tight leading-none mt-2">Import / Export</h1>
        </div>
      </div>

      <div className="p-10 grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Export card */}
        <div className="border border-neutral-200 bg-white p-6 space-y-5" data-testid="export-card">
          <div className="flex items-start justify-between">
            <div>
              <div className="label-eyebrow">01 — Export</div>
              <h2 className="font-display text-2xl tracking-tight mt-1">Download records</h2>
              <p className="text-sm text-neutral-500 mt-2 leading-relaxed">
                Export the ledger as CSV. Apply filters below to export a subset.
              </p>
            </div>
            <FileCsv size={28} weight="duotone" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="label-eyebrow mb-1.5">Region</div>
              <Select value={region} onValueChange={setRegion}>
                <SelectTrigger data-testid="export-region" className="rounded-none border-neutral-400 h-10"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Regions</SelectItem>
                  {regions.map((r) => <SelectItem key={r.id} value={r.name}>{r.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <div className="label-eyebrow mb-1.5">Status</div>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger data-testid="export-status" className="rounded-none border-neutral-400 h-10"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="open">Open</SelectItem>
                  <SelectItem value="returned">Returned</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <div className="label-eyebrow mb-1.5">Engineer</div>
              <Select value={engineerId} onValueChange={setEngineerId}>
                <SelectTrigger data-testid="export-engineer" className="rounded-none border-neutral-400 h-10"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Engineers ({engineers.length})</SelectItem>
                  {engineers.map((e) => (
                    <SelectItem key={e.id} value={e.id}>
                      {e.name}{e.region ? ` · ${e.region}` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <div className="label-eyebrow mb-1.5">Walk-in sales</div>
              <div className="flex gap-2" data-testid="export-walkin-chips">
                {[
                  { key: "all",     label: "Include" },
                  { key: "exclude", label: "Exclude" },
                  { key: "only",    label: "Only" },
                ].map((c) => (
                  <button
                    key={c.key}
                    type="button"
                    onClick={() => setWalkin(c.key)}
                    data-testid={`export-walkin-${c.key}`}
                    className={`h-10 flex-1 px-3 text-[11px] font-mono uppercase tracking-wider border transition-colors ${
                      walkin === c.key
                        ? "border-emerald-700 bg-emerald-700 text-white"
                        : "border-neutral-300 bg-white text-neutral-700 hover:border-emerald-700 hover:text-emerald-800"
                    }`}
                  >
                    {c.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="col-span-2">
              <div className="label-eyebrow mb-1.5">Packing lists</div>
              <div className="flex gap-2" data-testid="export-pl-chips">
                {[
                  { key: "exclude", label: "Exclude" },
                  { key: "include", label: "Include" },
                  { key: "only",    label: "Only" },
                ].map((c) => (
                  <button
                    key={c.key}
                    type="button"
                    onClick={() => setPl(c.key)}
                    data-testid={`export-pl-${c.key}`}
                    className={`h-10 flex-1 px-3 text-[11px] font-mono uppercase tracking-wider border transition-colors ${
                      pl === c.key
                        ? "border-teal-700 bg-teal-700 text-white"
                        : "border-neutral-300 bg-white text-neutral-700 hover:border-teal-700 hover:text-teal-800"
                    }`}
                  >
                    {c.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label htmlFor="date-from" className="label-eyebrow mb-1.5 block">Date from</Label>
              <Input
                id="date-from"
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                data-testid="export-date-from"
                className="rounded-none border-neutral-400 h-10"
              />
            </div>
            <div>
              <Label htmlFor="date-to" className="label-eyebrow mb-1.5 block">Date to</Label>
              <Input
                id="date-to"
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                data-testid="export-date-to"
                className="rounded-none border-neutral-400 h-10"
              />
            </div>
            <div className="col-span-2 flex items-center gap-1.5 flex-wrap">
              <button
                type="button"
                onClick={setThisMonth}
                data-testid="date-preset-this-month"
                className="text-[10px] font-mono uppercase tracking-wider px-2 py-1 border border-neutral-300 hover:border-neutral-900 hover:bg-neutral-900 hover:text-white"
              >
                This month
              </button>
              <button
                type="button"
                onClick={setLastMonth}
                data-testid="date-preset-last-month"
                className="text-[10px] font-mono uppercase tracking-wider px-2 py-1 border border-neutral-300 hover:border-neutral-900 hover:bg-neutral-900 hover:text-white"
              >
                Last month
              </button>
              <button
                type="button"
                onClick={savePreset}
                data-testid="save-preset-btn"
                className="text-[10px] font-mono uppercase tracking-wider px-2 py-1 border border-emerald-700 text-emerald-700 hover:bg-emerald-700 hover:text-white"
                title="Save current filter combo as a named preset"
              >
                ★ Save preset
              </button>
              <button
                type="button"
                onClick={resetFilters}
                data-testid="reset-filters"
                className="text-[10px] font-mono uppercase tracking-wider px-2 py-1 border border-neutral-300 text-neutral-500 hover:border-red-600 hover:text-red-700 ml-auto"
              >
                Reset all
              </button>
            </div>
            {presets.length > 0 && (
              <div className="col-span-2 border border-neutral-200 bg-neutral-50 p-2" data-testid="preset-strip">
                <div className="label-eyebrow mb-1.5">Saved presets</div>
                <div className="flex flex-wrap gap-1.5">
                  {presets.map((p) => (
                    <div key={p.id} className="inline-flex items-center gap-0.5 border border-neutral-300 bg-white" data-testid={`preset-${p.id}`}>
                      <button
                        type="button"
                        onClick={() => applyPreset(p)}
                        className="text-[10px] font-mono px-2 py-1 hover:bg-neutral-900 hover:text-white"
                        title="Load this preset"
                      >
                        {p.label}
                      </button>
                      <button
                        type="button"
                        onClick={() => deletePreset(p.id)}
                        className="text-[10px] px-1.5 py-1 text-neutral-400 hover:bg-red-600 hover:text-white"
                        title="Delete preset"
                        data-testid={`preset-delete-${p.id}`}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-2">
            <Button
              onClick={downloadXlsx}
              disabled={busy}
              data-testid="export-xlsx-btn"
              className="h-11 rounded-none bg-emerald-700 text-white hover:bg-emerald-800"
            >
              <DownloadSimple size={16} weight="bold" className="mr-2" /> Download Excel
            </Button>
            <Button
              onClick={downloadCsv}
              disabled={busy}
              data-testid="export-download-btn"
              variant="outline"
              className="h-11 rounded-none border-neutral-900 hover:bg-neutral-900 hover:text-white"
            >
              <DownloadSimple size={16} weight="bold" className="mr-2" /> Download CSV
            </Button>
          </div>
          <p className="text-[11px] text-neutral-500">
            One row per part line — never concatenated. <span className="font-mono">returned</span> = <span className="font-mono">Req</span> marks a requested part (pre-issue), <span className="font-mono">No</span> = issued, <span className="font-mono">Yes</span> = returned. Columns: <span className="font-mono">receipt_no · request_no · engineer_name · work_order_id · customer · parts_request · returned · parts_returned · QTY · faulty · returned_status · notes · created_by · created_at</span>.
          </p>
        </div>

        {/* Inventory Adjustments export card */}
        <div className="border border-neutral-200 bg-white p-6 space-y-5" data-testid="adjustments-card">
          <div className="flex items-start justify-between">
            <div>
              <div className="label-eyebrow">01b — Inventory adjustments</div>
              <h2 className="font-display text-2xl tracking-tight mt-1">Manager Adjustments</h2>
              <p className="text-sm text-neutral-500 mt-2 leading-relaxed">
                Every ± tweak a manager/supervisor made to an engineer's in-hand stock — with the mirrored main-stock movement and reason. Sorted newest first.
              </p>
            </div>
            <DownloadSimple size={28} weight="duotone" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Button
              onClick={() => downloadAdjustments("xlsx")}
              disabled={busy}
              data-testid="export-adjust-xlsx-btn"
              className="h-11 rounded-none bg-emerald-700 text-white hover:bg-emerald-800"
            >
              <DownloadSimple size={16} weight="bold" className="mr-2" /> Download Excel
            </Button>
            <Button
              onClick={() => downloadAdjustments("csv")}
              disabled={busy}
              data-testid="export-adjust-csv-btn"
              variant="outline"
              className="h-11 rounded-none border-neutral-900 hover:bg-neutral-900 hover:text-white"
            >
              <DownloadSimple size={16} weight="bold" className="mr-2" /> Download CSV
            </Button>
          </div>
          <p className="text-[11px] text-neutral-500">
            Columns: <span className="font-mono">date · adjusted_by · role · engineer · part · stock_code · group · engineer_qty_before · engineer_qty_after · delta · main_stock_before · main_stock_after · reason</span>.
          </p>
        </div>

        {/* Import card */}
        <div className="border border-neutral-200 bg-white p-6 space-y-5" data-testid="import-card">
          <div className="flex items-start justify-between">
            <div>
              <div className="label-eyebrow">02 — Import</div>
              <h2 className="font-display text-2xl tracking-tight mt-1">Upload CSV</h2>
              <p className="text-sm text-neutral-500 mt-2 leading-relaxed">
                Bulk import records. Engineers must already exist (matched by email or name).
              </p>
            </div>
            <UploadSimple size={28} weight="duotone" />
          </div>

          <label
            htmlFor="csv-file"
            className="block border-2 border-dashed border-neutral-300 hover:border-neutral-900 p-8 text-center cursor-pointer transition-colors"
            data-testid="import-dropzone"
          >
            <FileCsv size={28} weight="duotone" className="mx-auto mb-2" />
            <div className="text-sm font-medium">{file ? file.name : "Choose CSV file"}</div>
            <div className="text-xs text-neutral-500 mt-1">.csv up to a few MB</div>
            <input
              ref={fileRef}
              id="csv-file"
              type="file"
              accept=".csv,text/csv"
              className="hidden"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              data-testid="import-file-input"
            />
          </label>

          <Button
            onClick={doImport}
            disabled={busy || !file}
            data-testid="import-submit-btn"
            className="w-full h-11 rounded-none bg-neutral-900 text-white hover:bg-neutral-800 disabled:opacity-50"
          >
            <UploadSimple size={16} weight="bold" className="mr-2" /> Import Records
          </Button>

          {result && (
            <div className="border border-neutral-200 p-4 text-sm space-y-2" data-testid="import-result">
              <div className="flex items-center gap-2">
                <CheckCircle size={16} weight="fill" className="text-emerald-600" />
                <span><strong>{result.created}</strong> record(s) imported.</span>
              </div>
              {result.skipped?.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mt-2">
                    <XCircle size={16} weight="fill" className="text-amber-600" />
                    <span><strong>{result.skipped.length}</strong> skipped:</span>
                  </div>
                  <ul className="pl-6 mt-1 space-y-0.5 text-xs text-neutral-600 font-mono">
                    {result.skipped.slice(0, 10).map((s, i) => <li key={i}>{s}</li>)}
                    {result.skipped.length > 10 && <li>…and {result.skipped.length - 10} more</li>}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>

        {/* CSV format reference */}
        <div className="lg:col-span-2 border border-neutral-200 bg-white p-6 space-y-3">
          <div>
            <div className="label-eyebrow">Reference</div>
            <h3 className="font-display text-xl tracking-tight mt-1">CSV format</h3>
            <p className="text-sm text-neutral-500 mt-2">
              Parts are semicolon-separated as <span className="font-mono">Name:Quantity</span>. For returned parts append the status: <span className="font-mono">Name:Qty[used]</span> or <span className="font-mono">[unused]</span>.
            </p>
          </div>
          <pre className="bg-neutral-900 text-neutral-50 text-xs font-mono p-4 overflow-x-auto whitespace-pre-wrap">{sample}</pre>
          <div className="text-xs text-neutral-500">
            Columns: <span className="font-mono">engineer_email, engineer_name, region, parts_taken, taken_at, parts_returned, returned_at, status, notes</span>
          </div>
        </div>
      </div>
    </div>
  );
}
